import { Check } from "lucide-react";
import { useBookingDialog } from "@/contexts/BookingDialogContext";

const TIERS = [
  {
    id: "visit",
    name: "Visit only",
    summary: "Footfall + organic story coverage",
    points: [
      "Creator visits your venue",
      "Authentic story coverage",
      "1 location tag + mention",
      "Same-day posting",
    ],
    tone: "minimal",
  },
  {
    id: "shoot",
    name: "Shoot only",
    summary: "Studio-grade content, no live visit",
    points: [
      "Product / asset shipped to creator",
      "1 short-form vertical video",
      "3 hero stills",
      "30-day brand usage rights",
    ],
    tone: "minimal",
  },
  {
    id: "visit-shoot",
    name: "Visit + shoot",
    summary: "The signature on-location format",
    points: [
      "On-site half-day shoot",
      "2 vertical videos · 1 reel cut",
      "8 edited stills + raw files",
      "90-day brand usage rights",
    ],
    tone: "featured",
  },
  {
    id: "full",
    name: "Visit + shoot + digital sponsor",
    summary: "Full campaign with paid distribution",
    points: [
      "Everything in Visit + Shoot",
      "Sponsored posting on creator channels",
      "Paid amplification (Meta / TikTok)",
      "Performance report & analytics",
    ],
    tone: "primary",
  },
];

export default function ContractTiers() {
  const { openDialog } = useBookingDialog();
  return (
    <section
      id="contracts"
      data-testid="contract-tiers-section"
      className="py-24 sm:py-32 bg-[#FFFFFF]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[#276152] font-semibold">
            Contracts
          </p>
          <h2 className="font-serif text-5xl sm:text-6xl leading-[0.95] mt-4 text-[#0D3A35]">
            Four ways to <span className="text-[#276152]">work</span>{" "}
            with creators.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#0D3A35]/75">
            Choose the level of involvement that fits your brand and budget.
            Every contract is structured, scoped, and signed before a single
            frame is shot.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t, idx) => {
            const isPrimary = t.tone === "primary";
            const isFeatured = t.tone === "featured";
            return (
              <div
                key={t.id}
                data-testid={`contract-tier-${t.id}`}
                className={`relative rounded-2xl border p-7 flex flex-col min-h-[460px] transition-all duration-300 hover:-translate-y-1 ${
                  isPrimary
                    ? "bg-[#0D3A35] text-[#FFFFFF] border-[#0D3A35]"
                    : isFeatured
                    ? "bg-white border-[#0D3A35]/30 shadow-[0_30px_60px_-40px_rgba(13,58,53,0.4)]"
                    : "bg-white border-[#B1B7AB]/40"
                }`}
              >
                {isFeatured && (
                  <span className="absolute -top-3 left-7 inline-flex items-center px-3 py-1 rounded-full bg-[#276152] text-[#FFFFFF] text-[10px] uppercase tracking-[0.2em] font-semibold">
                    Most chosen
                  </span>
                )}
                <div className="flex items-center justify-between mb-6">
                  <span
                    className={`font-serif text-3xl ${
                      isPrimary ? "text-[#B1B7AB]" : "text-[#276152]"
                    }`}
                  >
                    0{idx + 1}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${
                      isPrimary ? "text-[#B1B7AB]" : "text-[#276152]"
                    }`}
                  >
                    Tier
                  </span>
                </div>

                <h3
                  className={`font-serif text-3xl leading-tight ${
                    isPrimary ? "text-[#FFFFFF]" : "text-[#0D3A35]"
                  }`}
                >
                  {t.name}
                </h3>
                <p
                  className={`mt-2 text-sm ${
                    isPrimary ? "text-[#FFFFFF]/70" : "text-[#0D3A35]/65"
                  }`}
                >
                  {t.summary}
                </p>

                <ul className="mt-7 space-y-3 flex-1">
                  {t.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm">
                      <Check
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          isPrimary ? "text-[#B1B7AB]" : "text-[#276152]"
                        }`}
                        strokeWidth={2}
                      />
                      <span
                        className={
                          isPrimary
                            ? "text-[#FFFFFF]/90"
                            : "text-[#0D3A35]/85"
                        }
                      >
                        {p}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => openDialog(t.id)}
                  data-testid={`tier-cta-${t.id}`}
                  className={`mt-8 inline-flex items-center justify-between text-sm font-medium ${
                    isPrimary
                      ? "text-white hover:text-white/85"
                      : "text-[#0D3A35] hover:text-[#276152]"
                  } transition-colors`}
                >
                  Request this tier
                  <span aria-hidden>→</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
